import { useEffect } from 'react'
import { useContent } from '../../contexts/ContentContext'
import SectionLabel from '../ui/SectionLabel'
import AccordionWrap from '../ui/AccordionWrap'
import { ChevronDown } from 'lucide-react'
import { useNavigation } from '../../contexts/NavigationContext'
import ScrollableSection from '../ui/ScrollableSection'
import { useFAQScroll } from '../../hooks/useFAQScroll'

/** Render content **segments** as bold+italic (no raw asterisks). */
function formatAnswerInline(text) {
  const nodes = []
  const re = /\*\*(.+?)\*\*/g
  let last = 0
  let m
  let key = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    nodes.push(
      <strong key={key++} className="font-semibold italic text-color-primary">
        {m[1]}
      </strong>
    )
    last = m.index + m[0].length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes.length ? nodes : text
}

/**
 * FAQSection — FAQ section with composite typography utilities
 *
 * MIGRATED to composite typography utilities (Single Source of Truth):
 * - Label: typography-label (includes font-family, size, color)
 * - Headline: typography-h2 (includes font-family, size, color, leading)
 * - Question: typography-h4 (includes font-family, size, color)
 * - Answer: typography-body (includes font-family, size, color, leading)
 *
 * CRITICAL PRESERVED:
 * - Section with id="faq" (scroll target)
 * - NavigationContext for expandedFAQIndex (Single Source of Truth)
 * - Only one item expanded at a time
 * - Map over faq.items
 *
 * Scroll: soft-snap while in view (data-faq-section-active) + instant pin on open.
 */
export default function FAQSection() {
  const { faq } = useContent()
  const { expandedFAQIndex, setExpandedFAQIndex } = useNavigation()
  const toggleExpand = useFAQScroll(expandedFAQIndex, setExpandedFAQIndex)

  // Soften mandatory → proximity while FAQ is on screen (same class as Cases).
  useEffect(() => {
    const section = document.getElementById('faq')
    if (!section) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          document.documentElement.setAttribute('data-faq-section-active', 'true')
        } else {
          document.documentElement.removeAttribute('data-faq-section-active')
        }
      },
      { threshold: 0 }
    )
    observer.observe(section)

    return () => {
      observer.disconnect()
      document.documentElement.removeAttribute('data-faq-section-active')
    }
  }, [])

  return (
    <ScrollableSection data-section="FAQSection"
      id="faq"
      hasAccordion={true}
      className="bg-color-bg-light"
      data-testid="faq-section"
    >
        <SectionLabel text={faq.bigLabel} />

        <h2 className="typography-h2-mobile md:typography-h2 leading-tight heading-body-spacing">
          {faq.headline}
        </h2>

        <div>
          {faq.items.map((item, index) => (
            <div id={`faq-${index}`} key={index} className="scroll-mt-20">
              <button
                onClick={(e) => toggleExpand(index, e)}
                className="w-full flex justify-between items-start gap-4 py-4 text-left font-primary text-color-secondary hover:text-color-secondary/80 transition-colors pr-1"
                data-testid={`faq-${index}`}
              >
                <span className="typography-h4 leading-tight">{item.question}</span>
                <ChevronDown
                  className={`chevron flex-shrink-0 transition-transform duration-500 ease-out mt-[10px] ${
                    expandedFAQIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <AccordionWrap isOpen={expandedFAQIndex === index}>
                <div className="pb-4">
                  {item.answer.split('\n\n').map((paragraph, i, arr) => (
                    <p
                      key={i}
                      className={`typography-body whitespace-pre-line ${
                        i < arr.length - 1 ? 'paragraph-spacing' : ''
                      }`}
                    >
                      {formatAnswerInline(paragraph)}
                    </p>
                  ))}
                </div>
              </AccordionWrap>
            </div>
          ))}
        </div>
    </ScrollableSection>
  )
}
